/**
 * TransporterDashboardBody — main-dashboard body for `transporter` persona.
 *
 * Replaces StandardFeed/SmartMatches (which are dog-registry oriented and
 * useless to a transporter) with state-aware content driven by
 * `GET /api/me/transporter/application-status`.
 *
 * State machine (mirrors the backend resolver):
 *   - "none"        → Big "Become a verified transporter" panel with a
 *                     3-step path, a "What you'll need" checklist, and
 *                     a primary "Start application" CTA → /transport/apply.
 *   - "pending"     → "Under review" status panel + View application link.
 *   - "needs_info"  → Warning panel + "Update application" CTA.
 *   - "rejected"    → Polite explanation, no CTA.
 *   - "approved"    → Live dispatch summary: open bid invitations preview,
 *                     active trips list, lifetime earnings tile, with
 *                     deep links to /transporter.
 *
 * Why this lives separately from /transporter (TransporterDashboard.jsx):
 *   The /transporter page is the *operational* dashboard for approved
 *   transporters only — it 403s for everyone else. The MAIN dashboard
 *   (/dashboard) is what every signed-in user lands on, and a transporter
 *   persona who hasn't applied yet (or whose application is in review)
 *   needs guidance, not a 403. This component closes that gap.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LuxuryCard, Indicium } from "@/components/luxury";
import { TruckIcon as Truck, ShieldCheckIcon as ShieldCheck, FileTextIcon as FileText, CheckCircleIcon as CheckCircle, ClockIcon as Clock, WarningIcon as Warning, CurrencyDollarIcon as CurrencyDollar, ArrowRightIcon as ArrowRight, MapPinIcon as MapPin, ChartBarIcon as ChartBar, ClipboardTextIcon as ClipboardText } from "@phosphor-icons/react";

export default function TransporterDashboardBody() {
  const [status, setStatus] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/me/transporter/application-status")
      .then((r) => { if (!cancelled) setStatus(r.data || { state: "none" }); })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, []);

  // Don't render a broken empty state if the status fetch failed — fall
  // back to the apply panel so the user always has a way forward.
  if (loadFailed) return <ApplyPanel />;
  if (!status) return <SkeletonPanel />;

  const state = status.state || "none";
  if (state === "approved") return <ApprovedPanel />;
  if (state === "pending") return <PendingPanel submittedAt={status.submitted_at} />;
  if (state === "needs_info") return <NeedsInfoPanel />;
  if (state === "rejected") return <RejectedPanel />;
  return <ApplyPanel />;
}

/* ============================================================
   Panel: NONE — user picked transporter persona but never applied
   ============================================================ */
function ApplyPanel() {
  return (
    <div className="space-y-6" data-testid="transporter-body-apply">
      <LuxuryCard className="p-6 sm:p-8">
        <div className="flex items-start gap-4 mb-5">
          <Indicium icon={Truck} tone="gold" size="lg" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-1.5">
              Armstrong Transport
            </p>
            <h2 className="font-spectral font-light text-3xl sm:text-4xl tracking-tight leading-[1.1]">
              Become a verified transporter
            </h2>
            <p className="text-sm sm:text-base text-[#4A4A4A] mt-2 leading-relaxed max-w-2xl">
              Bid on real ground-transport routes from breeders and buyers
              across the country. Get paid via Stripe Connect escrow —
              funds release the moment delivery is confirmed.
            </p>
          </div>
        </div>

        <div className="gold-hairline mb-5" />

        {/* 3-step path */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Step
            num="1"
            title="Apply"
            body="Submit your USDA license, insurance, and signed Transporter Agreement."
          />
          <Step
            num="2"
            title="Get reviewed"
            body="Our team reviews every application — typically within 3 business days."
          />
          <Step
            num="3"
            title="Start bidding"
            body="Approved transporters see live bid invitations matched to their service area."
          />
        </div>

        {/* What you'll need checklist */}
        <div className="rounded-xl bg-cream/50 border border-[#E8D9A6] p-5 mb-6" data-testid="transporter-requirements-checklist">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-gold-dark mb-3">
            What you'll need
          </p>
          <ul className="space-y-2.5 text-sm text-ink">
            <ReqItem icon={ShieldCheck} label="USDA commercial transport license" hint="Number, expiry date, and a photo or PDF of the license itself." />
            <ReqItem icon={FileText} label="Certificate of Insurance" hint="Minimum $300,000 commercial liability. Carrier name, policy number, and expiry." />
            <ReqItem icon={MapPin} label="Service area" hint="The U.S. states you regularly cover — bid invitations only route to matches." />
            <ReqItem icon={ClipboardText} label="Acceptance of the Transporter Agreement v1.0" hint="Independent-contractor terms, 10% platform fee, escrow rules, cancellation table." />
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/transport/apply" className="sm:flex-shrink-0">
            <Button
              className="w-full sm:w-auto bg-ink hover:bg-crimson text-white rounded-full px-7 h-12 text-[15px] font-semibold"
              data-testid="transporter-start-application-btn"
            >
              Start application <ArrowRight size={16} weight="bold" className="ml-1.5" />
            </Button>
          </Link>
          <Link to="/transport/getting-started" className="sm:flex-shrink-0">
            <Button
              variant="outline"
              className="w-full sm:w-auto rounded-full border-[#E5E5E5] h-12 px-6 text-[14px]"
              data-testid="transporter-learn-more-btn"
            >
              Learn how it works
            </Button>
          </Link>
        </div>
      </LuxuryCard>

      {/* Secondary card: what's in it for the transporter */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <BenefitCard
          icon={CurrencyDollar}
          title="Paid via Stripe Connect"
          body="Direct deposit on delivery confirmation. No invoicing, no chasing buyers."
        />
        <BenefitCard
          icon={ChartBar}
          title="Live bid invitations"
          body="See open routes the moment a buyer posts one — quote competitively."
        />
        <BenefitCard
          icon={ShieldCheck}
          title="Buyer escrow protection"
          body="Funds held in escrow until delivery — both sides protected from cancellations."
        />
      </div>
    </div>
  );
}

/* ============================================================
   Panel: PENDING — application submitted, waiting on review
   ============================================================ */
function PendingPanel({ submittedAt }) {
  return (
    <LuxuryCard className="p-6 sm:p-8" data-testid="transporter-body-pending">
      <div className="flex items-start gap-4 mb-5">
        <Indicium icon={Clock} tone="gold" size="lg" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-1.5">
            Under review
          </p>
          <h2 className="font-spectral font-light text-3xl tracking-tight leading-tight">
            Your application is in review
          </h2>
          <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed max-w-2xl">
            We typically respond within 3 business days. We'll email you the
            moment a decision is in — no need to refresh.
            {submittedAt && (
              <>
                {" "}Submitted{" "}
                <span className="font-tabular text-ink">
                  {new Date(submittedAt).toLocaleDateString()}
                </span>.
              </>
            )}
          </p>
        </div>
      </div>

      <div className="gold-hairline mb-5" />

      <ReviewTimeline currentStep={2} />

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <Link to="/transport/apply">
          <Button
            variant="outline"
            className="rounded-full border-[#E5E5E5] h-11 px-6"
            data-testid="transporter-view-application-btn"
          >
            View application
          </Button>
        </Link>
        <Link to="/transport/getting-started">
          <Button
            variant="ghost"
            className="rounded-full h-11 px-4 text-sm text-[#6B6B6B] hover:text-ink"
          >
            How bidding works
          </Button>
        </Link>
      </div>
    </LuxuryCard>
  );
}

/* ============================================================
   Panel: NEEDS_INFO — reviewer flagged something
   ============================================================ */
function NeedsInfoPanel() {
  return (
    <LuxuryCard className="p-6 sm:p-8 border-[#C1272D]/30" data-testid="transporter-body-needs-info">
      <div className="flex items-start gap-4 mb-5">
        <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-crimson/10 text-crimson flex-shrink-0">
          <Warning size={24} weight="duotone" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-1.5">
            Action needed
          </p>
          <h2 className="font-spectral font-light text-3xl tracking-tight leading-tight">
            We need a quick clarification on your application
          </h2>
          <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed max-w-2xl">
            Our review team flagged something on your submission. Open
            your application to see what's needed — most commonly a
            clearer license photo or a fresh certificate of insurance.
          </p>
        </div>
      </div>
      <Link to="/transport/apply">
        <Button
          className="bg-ink hover:bg-crimson text-white rounded-full px-6 h-11"
          data-testid="transporter-update-application-btn"
        >
          Update application <ArrowRight size={14} weight="bold" className="ml-1.5" />
        </Button>
      </Link>
    </LuxuryCard>
  );
}

/* ============================================================
   Panel: REJECTED — application declined this round
   ============================================================ */
function RejectedPanel() {
  return (
    <LuxuryCard className="p-6 sm:p-8" data-testid="transporter-body-rejected">
      <div className="flex items-start gap-4">
        <Indicium icon={Warning} tone="ink" size="lg" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#6B6B6B] mb-1.5">
            Application status
          </p>
          <h2 className="font-spectral font-light text-3xl tracking-tight leading-tight">
            Your application wasn't approved this round
          </h2>
          <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed max-w-2xl">
            You can reapply once the issue noted in your review email is
            resolved — typically a license or insurance gap. We're happy
            to take another look.
          </p>
        </div>
      </div>
    </LuxuryCard>
  );
}

/* ============================================================
   Panel: APPROVED — live dispatch summary
   ============================================================ */
function ApprovedPanel() {
  const [data, setData] = useState(null);
  const [bids, setBids] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/me/transporter/dashboard").catch(() => ({ data: null })),
      api.get("/me/transporter/open-requests").catch(() => ({ data: { requests: [] } })),
    ]).then(([d, o]) => {
      if (cancelled) return;
      setData(d.data || null);
      setBids((o.data?.requests || []).slice(0, 3));
    });
    return () => { cancelled = true; };
  }, []);

  const orders = data?.orders_by_status || {};
  const active = [...(orders.booked || []), ...(orders.in_transit || [])];
  const lifetime = data?.lifetime_earnings_usd;
  const completed = (orders.completed || []).length;

  return (
    <div className="space-y-6" data-testid="transporter-body-approved">
      {/* Stat strip — transporter-relevant numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <StatTile
          icon={ChartBar}
          label="Open bids"
          value={bids.length || 0}
          to="/transporter?focus=bids"
          testId="transporter-stat-bids"
        />
        <StatTile
          icon={Truck}
          label="Active trips"
          value={active.length}
          to="/transporter"
          testId="transporter-stat-active"
        />
        <StatTile
          icon={CheckCircle}
          label="Completed"
          value={completed}
          to="/transporter"
          testId="transporter-stat-completed"
        />
        <StatTile
          icon={CurrencyDollar}
          label="Lifetime earned"
          value={lifetime != null ? `$${Math.round(lifetime).toLocaleString()}` : "—"}
          to="/transporter"
          testId="transporter-stat-earnings"
        />
      </div>

      {/* Open bid invitations preview */}
      <LuxuryCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-3">
            <Indicium icon={ChartBar} tone="ink" size="sm" />
            Open bid invitations
          </h2>
          <Link
            to="/transporter?focus=bids"
            className="text-sm text-ink hover:text-crimson transition-colors"
            data-testid="transporter-view-all-bids"
          >
            View all
          </Link>
        </div>
        <div className="gold-hairline mb-2" />
        <div className="divide-y divide-[#E5E5E5]">
          {bids.length === 0 && (
            <div className="text-sm text-[#6B6B6B] py-6 text-center">
              No bid invitations right now. New ones land here the moment a
              buyer posts one in your service area.
            </div>
          )}
          {bids.map(({ order, my_quote }) => (
            <Link
              key={order.id}
              to={`/transporter?focus=bids#${order.id}`}
              className="flex items-center gap-3 py-3 hover:bg-cream/40 -mx-2 px-2 rounded-lg transition-colors"
              data-testid={`transporter-bid-${order.id}`}
            >
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-gold/15 text-gold-dark flex-shrink-0">
                <MapPin size={16} weight="duotone" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {(order.pickup_city || order.pickup_state || "Pickup")} → {(order.dropoff_city || order.dropoff_state || "Dropoff")}
                </div>
                <div className="text-xs text-[#6B6B6B] truncate">
                  {my_quote ? "You've bid — buyer reviewing" : "Open for bids"}
                </div>
              </div>
              <ArrowRight size={14} weight="bold" className="text-[#6B6B6B] flex-shrink-0" />
            </Link>
          ))}
        </div>
      </LuxuryCard>

      {/* Active trips quick list */}
      {active.length > 0 && (
        <LuxuryCard className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-3">
              <Indicium icon={Truck} tone="gold" size="sm" />
              Active trips
            </h2>
            <Link
              to="/transporter"
              className="text-sm text-ink hover:text-crimson transition-colors"
            >
              Dispatch board
            </Link>
          </div>
          <div className="gold-hairline mb-2" />
          <div className="divide-y divide-[#E5E5E5]">
            {active.slice(0, 5).map((o) => (
              <Link
                key={o.id}
                to={`/transport/orders/${o.id}`}
                className="flex items-center gap-3 py-3 hover:bg-cream/40 -mx-2 px-2 rounded-lg transition-colors"
                data-testid={`transporter-active-trip-${o.id}`}
              >
                <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-ink/5 text-ink flex-shrink-0">
                  <Truck size={16} weight="duotone" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {(o.pickup_city || o.pickup_state || "Pickup")} → {(o.dropoff_city || o.dropoff_state || "Dropoff")}
                  </div>
                  <div className="text-xs text-[#6B6B6B] truncate capitalize">
                    {o.status?.replace(/_/g, " ") || "booked"}
                  </div>
                </div>
                <ArrowRight size={14} weight="bold" className="text-[#6B6B6B] flex-shrink-0" />
              </Link>
            ))}
          </div>
        </LuxuryCard>
      )}
    </div>
  );
}

/* ---------------- helpers ---------------- */

function Step({ num, title, body }) {
  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-ink text-gold font-tabular text-xs font-bold">
          {num}
        </span>
        <p className="font-spectral text-lg tracking-tight">{title}</p>
      </div>
      <p className="text-sm text-[#6B6B6B] leading-relaxed">{body}</p>
    </div>
  );
}

function ReqItem({ icon: Icon, label, hint }) {
  return (
    <li className="flex items-start gap-3">
      <Icon size={18} weight="duotone" className="text-gold-dark mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-medium leading-snug">{label}</p>
        <p className="text-xs text-[#6B6B6B] leading-relaxed mt-0.5">{hint}</p>
      </div>
    </li>
  );
}

function BenefitCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-5">
      <Icon size={22} weight="duotone" className="text-gold-dark mb-2" />
      <p className="font-spectral text-lg tracking-tight mb-1">{title}</p>
      <p className="text-sm text-[#6B6B6B] leading-relaxed">{body}</p>
    </div>
  );
}

function ReviewTimeline({ currentStep }) {
  const steps = [
    { label: "Application submitted", n: 1 },
    { label: "Under review", n: 2 },
    { label: "Decision emailed", n: 3 },
  ];
  return (
    <ol className="space-y-3" data-testid="transporter-review-timeline">
      {steps.map((s) => {
        const done = s.n < currentStep;
        const active = s.n === currentStep;
        return (
          <li key={s.n} className="flex items-center gap-3">
            <span
              className={`inline-flex items-center justify-center h-7 w-7 rounded-full flex-shrink-0 ${
                done
                  ? "bg-[#0F8043] text-white"
                  : active
                    ? "bg-gold text-ink ring-4 ring-gold/20"
                    : "bg-[#E5E5E5] text-[#6B6B6B]"
              }`}
            >
              {done ? <CheckCircle size={14} weight="fill" /> : <span className="font-tabular text-xs font-bold">{s.n}</span>}
            </span>
            <span className={`text-sm ${active ? "font-semibold text-ink" : "text-[#4A4A4A]"}`}>
              {s.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function StatTile({ icon: Icon, label, value, to, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="rounded-xl border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] p-4 transition-colors flex flex-col items-start gap-2"
    >
      <Indicium icon={Icon} tone="gold" size="sm" />
      <span className="font-spectral text-3xl tabular-nums leading-none text-ink">
        {value}
      </span>
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B]">
        {label}
      </span>
    </Link>
  );
}

function SkeletonPanel() {
  return (
    <LuxuryCard className="p-6 sm:p-8" data-testid="transporter-body-skeleton">
      <div className="h-6 w-40 bg-[#EEE] rounded animate-pulse mb-3" />
      <div className="h-10 w-80 max-w-full bg-[#EEE] rounded animate-pulse mb-2" />
      <div className="h-4 w-full max-w-xl bg-[#EEE] rounded animate-pulse mb-6" />
      <div className="h-12 w-40 bg-[#EEE] rounded-full animate-pulse" />
    </LuxuryCard>
  );
}
